import { useState, type MouseEvent } from 'react'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { PhoneOutgoing } from 'lucide-react'
import { Button } from '@highlight/ui'

// First in the post-hero stagger: the header holds this row out of layout for
// the hero's fade (see header.tsx's ctaEntrance), so no delay here — it fades
// up the moment it mounts, and the brief follows ~80ms behind (sections
// wrapper, body.tsx). Strong ease-out so the entrance reads as deliberate.
const enterVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0.23, 1, 0.32, 1] } },
}

interface MeetingJoinButtonProps {
  joinLabel: string
  isAwaitingJoin: boolean
  onClick: (e: MouseEvent) => void
  /**
   * Play the slide-up entrance on mount — the header instance taking over from
   * the Get Ready hero after a brief lands. The hero's matching exit lives on
   * its wrapper in body.tsx.
   */
  animateIn?: boolean
}

/**
 * The Join CTA, shared by the header CTA row (`header.tsx`) and the Get Ready
 * hero (`get-ready.tsx`). Reduced motion drops the entrance for an instant swap.
 */
export function MeetingJoinButton({ joinLabel, isAwaitingJoin, onClick, animateIn = false }: MeetingJoinButtonProps) {
  const shouldReduceMotion = useReducedMotion()
  // Latched at mount: `animateIn` is only true on the render where the swap
  // happens, and un-wrapping later would remount the button mid-animation.
  const [entered] = useState(animateIn)

  const button = (
    <Button
      variant="primary"
      tone="inverse"
      className="w-full"
      left={<PhoneOutgoing aria-hidden="true" />}
      loading={isAwaitingJoin}
      onClick={onClick}
    >
      {joinLabel}
    </Button>
  )

  if (shouldReduceMotion || !entered) {
    return button
  }

  return (
    <motion.div variants={enterVariants} initial="hidden" animate="visible">
      {button}
    </motion.div>
  )
}
